import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Button from "../components/Button";
import { ReactComponent as SaudiRiyalSymbol } from "../assets/saudi_riyal_symbol.svg";
import { useLanguage } from "../contexts/LanguageContext";
import { CouponInput } from "../components/dashboardPage_components/affiliate-referrals";
import { useCoupons } from "../hooks/useCoupons";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { trackTikTokCommerce } from "../utils/tiktok";
import { trackGa4 } from "../utils/analytics";
import "../styles/Global.css";
import styles from "./PaymentConfirmation.module.css";

// This helper now only maps plan slugs to their translated names and features
const getPlanDetails = (
  planSlug: string,
  t: any
): { name: string; features: string[]; duration?: string } | null => {
  if (!t || !t.pricing) {
    return null;
  }

  const planMappings: { [key: string]: any } = {
    top_up: t.pricing.topup,
    premium: t.pricing.premium,
    premium_plus: t.pricing.premiumPlus,
  };

  const details = planMappings[planSlug];
  if (!details) return null;

  let features: string[] = [];
  if (planSlug === "top_up") {
    features = details.features.filter(
      (feature: string) => feature && feature.trim() !== ""
    );
  } else {
    features = [details.credits, t.common.sameDay].filter(
      (feature: string) => feature && feature.trim() !== ""
    );
  }

  return {
    name: details.name,
    features,
    duration: details.validityPeriod || details.duration,
  };
};

declare global {
  interface Window {
    Moyasar: any;
  }
}

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const PaymentConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [planDetails, setPlanDetails] = useState<{
    name: string;
    features: string[];
    duration?: string;
  } | null>(null);
  const [formKey, setFormKey] = useState<number>(Date.now());
  const [invoiceInfo, setInvoiceInfo] = useState<{
    id: string;
    amount_halala: number;
    plan: string;
  } | null>(null);
  const [originalAmount, setOriginalAmount] = useState<number>(0);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState(false);
  const [isSaudiUser, setIsSaudiUser] = useState(true);
  const { applyCouponToInvoice, isLoading: isCouponLoading } = useCoupons();

  // Fetch location status to determine if the note should be shown
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const res = await fetch(`${API_URL}/api/pricing`);
        if (!res.ok) return;
        const json = await res.json();
        setIsSaudiUser(json?.is_saudi_user ?? true);
      } catch (err) {
        console.error("Location fetch error:", err);
      }
    };
    fetchLocation();
  }, []);

  // Fetch invoice details by id from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const invoiceId = params.get("invoice_id");
    if (!invoiceId) return;

    const fetchInvoice = async () => {
      try {
        const res = await authenticatedFetch(`${API_URL}/api/invoices/${invoiceId}`);
        if (!res.ok) throw new Error("Invoice not found");
        const json = await res.json();
        setInvoiceInfo({
          id: json.id,
          amount_halala: json.amount_halala,
          plan: json.plan,
        });
        setOriginalAmount(json.amount_halala);
      } catch (err) {
        console.error(err);
        (window as any).showNotification?.(
          "Invalid or expired invoice",
          "error"
        );
        navigate("/pricing");
      }
    };

    fetchInvoice();
  }, [location.search, navigate]);

  // Derive the translated plan details
  useEffect(() => {
    if (!invoiceInfo || !t || !t.pricing) return;
    const details = getPlanDetails(invoiceInfo.plan, t);
    if (details) {
      setPlanDetails(details);
    }
  }, [invoiceInfo, t]);

  // Initialize Moyasar form (ONLY if Saudi user)
  useEffect(() => {
    if (invoiceInfo && planDetails && window.Moyasar && isSaudiUser) {
      initializeMoyasarForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey, invoiceInfo, planDetails, isSaudiUser]);

  useEffect(() => {
    const consentChanged = () => {
      if (invoiceInfo && isSaudiUser && document.querySelector(".mysr-form form")) {
        trackTikTokCommerce("InitiateCheckout", invoiceInfo.id);
      }
    };
    window.addEventListener("cshaped-tiktok-consent", consentChanged);
    return () => window.removeEventListener("cshaped-tiktok-consent", consentChanged);
  }, [invoiceInfo, isSaudiUser]);

  const initializeMoyasarForm = () => {
    if (!planDetails || !invoiceInfo || !isSaudiUser) return;
    const formContainer = document.querySelector(".mysr-form");
    if (!formContainer || !window.Moyasar) return;

    try {
      window.Moyasar.init({
        element: ".mysr-form",
        amount: invoiceInfo.amount_halala,
        currency: "SAR",
        description: `C-Shaped ${planDetails.name} Plan`,
        metadata: {
          invoice_id: invoiceInfo.id,
        },
        publishable_api_key:
          process.env.REACT_APP_MOYASAR_PUBLISHABLE_KEY || "",
        callback_url: `${window.location.origin}/payment-callback?invoice_id=${invoiceInfo.id}`,
        methods: ["creditcard", "mada"],
      });
      trackTikTokCommerce("InitiateCheckout", invoiceInfo.id);
      trackGa4("begin_checkout", {
        currency: "SAR",
        value: invoiceInfo.amount_halala / 100,
        invoice_id: invoiceInfo.id,
        plan: planDetails.name,
      }, { once: `begin_checkout:${invoiceInfo.id}` });
    } catch (error) {
      console.error("Error initializing Moyasar form:", error);
    }
  };

  const handleGoBack = () => {
    navigate("/pricing");
  };

  const handleApplyCoupon = async () => {
    if (!invoiceInfo || !couponCode.trim()) return;

    setCouponError(false);
    const result = await applyCouponToInvoice(invoiceInfo.id, couponCode);

    if (result.success) {
      try {
        const res = await authenticatedFetch(`${API_URL}/api/invoices/${invoiceInfo.id}`);
        if (!res.ok) throw new Error("Failed to refresh invoice");
        const json = await res.json();

        setInvoiceInfo({
          id: json.id,
          amount_halala: json.amount_halala,
          plan: json.plan,
        });

        setFormKey(Date.now());
        (window as any).showNotification?.(result.message, "success");
      } catch (err) {
        (window as any).showNotification?.(
          "Coupon applied but failed to refresh. Please reload.",
          "warning"
        );
      }
    } else {
      (window as any).showNotification?.(result.message, "error");
      setCouponError(true);
    }
  };

  const isMobile = window.innerWidth <= 768;

  if (!invoiceInfo || !planDetails) {
    return (
      <div className="page-container">
        <Header />
        <main className="main-content">
          <div style={{ textAlign: "center", padding: "50px" }}>
            <p>Loading payment details...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isDiscountApplied = invoiceInfo.amount_halala < originalAmount;
  const displayPrice = (invoiceInfo.amount_halala / 100).toFixed(0);
  const originalDisplayPrice = isDiscountApplied
    ? (originalAmount / 100).toFixed(0)
    : null;

  return (
    <div className="page-container">
      <Header />
      <main className={`main-content ${styles.confirmationPage}`}>
        <div className={styles.confirmationContainer}>
          {/* Header */}
          <div className={styles.pageHeader}>
            <h1 className={styles.pageTitle}>
              {t.pricing.payment.confirmationTitle}
            </h1>
            <p className={styles.pageSubtitle}>
              {t.pricing.payment.confirmationSubtitle}
            </p>
          </div>

          {/* Cards */}
          <div
            className={
              isMobile ? styles.cardsLayoutMobile : styles.cardsLayout
            }
          >
            {/* Plan Summary Card */}
            <div
              className={`${styles.card} ${isMobile ? styles.planCardMobile : styles.planCard
                }`}
            >
              <h2 className={styles.planName}>{planDetails.name}</h2>
              {planDetails.duration && (
                <p className={styles.planDuration}>{planDetails.duration}</p>
              )}

              <div className={styles.priceDisplay}>
                <SaudiRiyalSymbol className={styles.riyalSymbol} />
                {displayPrice}
                {isDiscountApplied && (
                  <span className={styles.originalPrice}>
                    {originalDisplayPrice}
                  </span>
                )}
              </div>

              {!isSaudiUser && (
                <div className={styles.internationalNote}>
                  *The amount shown is in SAR. For international users, this is
                  approximately $
                  {((invoiceInfo.amount_halala / 100) * 0.267).toFixed(0)} USD.
                </div>
              )}

              <div className={styles.featuresBox}>
                <h3 className={styles.featuresTitle}>
                  {t.pricing.payment.whatYouGet}
                </h3>
                {planDetails.features.map((feature, index) => (
                  <div key={index} className={styles.featureItem}>
                    <span className={styles.featureCheckmark}>✓</span>
                    <span className={styles.featureText}>{feature}</span>
                  </div>
                ))}
              </div>

              <Button onClick={handleGoBack} className={styles.backButton}>
                {isRTL ? "العودة للخطط ←" : t.pricing.payment.backToPlans}
              </Button>
            </div>

            {/* Payment Form Card */}
            <div
              className={`${styles.card} ${isMobile ? styles.paymentCardMobile : styles.paymentCard
                }`}
            >
              <h2 className={styles.paymentTitle}>
                {t.pricing.payment.paymentDetails}
              </h2>
              <p className={styles.paymentSubtitle}>
                {t.pricing.payment.enterCardDetails}
              </p>

              {/* Coupon Section */}
              <div className={styles.couponSection}>
                <h3 className={styles.couponLabel}>Have a coupon code?</h3>
                <div className={styles.couponRow}>
                  <CouponInput
                    code={couponCode}
                    onCodeChange={setCouponCode}
                    disabled={isCouponLoading || isDiscountApplied}
                    isValidating={isCouponLoading}
                    error={couponError}
                    placeholder="Enter coupon code"
                  />
                  <Button
                    onClick={handleApplyCoupon}
                    disabled={
                      isCouponLoading || !couponCode || isDiscountApplied
                    }
                    className={`${styles.couponButton} ${isCouponLoading ? styles.couponButtonLoading : ""
                      }`}
                  >
                    {isCouponLoading
                      ? "..."
                      : isDiscountApplied
                        ? "Applied"
                        : "Apply"}
                  </Button>
                </div>
              </div>

              {/* Payment Form */}
              <div className={styles.paymentFormContainer}>
                {isSaudiUser ? (
                  <div key={formKey} className="mysr-form"></div>
                ) : (
                  <p className={styles.checkoutUnavailable}>
                    Online checkout is currently available only in Saudi Arabia.
                    Please contact support for payment assistance.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentConfirmation;
